import { FormEvent, useCallback, useEffect, useState } from 'react';
import { dealerErrorCode, dealerErrorMessage, dealersApi } from '../../services/dealersApi';
import { DealerLot, DealerRefund } from '../../types/dealers';
import { describedBy, FormField } from '../promotions/PromoFormParts';
import DealerStatusChip from './DealerStatusChip';
import RevokeAfterPaidDialog from './RevokeAfterPaidDialog';
import { aed, formatDate, humanize, todayDubai } from './dealerFormat';

interface DealerBalanceSectionProps {
  dealerId: string;
  token: string;
  canRevokeAfterPaid: boolean;
  notify: (message: string, type?: 'success' | 'error') => void;
  onFeatureOff: () => void;
  /**
   * The prepaid balance (lots) — a dealer wallet surface. False while the wallet switch is known off: the lots
   * are neither fetched nor shown, and only the refunds owed to the dealer remain (Maher 2026-11-03).
   */
  showBalance?: boolean;
  onChanged?: () => void;
  refreshKey?: number;
}

/**
 * Spec 085 — the dealer's prepaid balance (lots from packs, returned and refunded credit) and the
 * refunds Garagat owes it after a revoke-after-paid. A dealer refund is marked paid once the bank
 * transfer is out (open while the switch is off: the cash is already owed).
 */
export default function DealerBalanceSection({ dealerId, token, canRevokeAfterPaid, notify, onFeatureOff, onChanged, refreshKey = 0, showBalance = true }: DealerBalanceSectionProps) {
  const [lots, setLots] = useState<DealerLot[]>([]);
  const [refunds, setRefunds] = useState<DealerRefund[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<DealerLot | null>(null);
  const [paying, setPaying] = useState<DealerRefund | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [l, r] = await Promise.all([
        showBalance ? dealersApi.lots(dealerId, token) : Promise.resolve([] as DealerLot[]),
        dealersApi.dealerRefunds(dealerId, token),
      ]);
      setLots(l);
      setRefunds(r);
    } catch (err) {
      setError(dealerErrorMessage(err, 'Failed to load the dealer balance'));
    } finally {
      setLoading(false);
    }
  }, [dealerId, token, showBalance]);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (loading) return <div className="admin-loading"><div className="admin-loading-spinner" /></div>;
  if (error) {
    return (
      <div>
        <div className="admin-info-banner danger" role="alert">{error}</div>
        <button type="button" className="admin-btn admin-btn-outline admin-btn-sm" onClick={load}>Retry</button>
      </div>
    );
  }

  return (
    <div data-testid="dealer-balance">
      {showBalance && <h3 className="admin-card-title dealer-section-title">Dealer balance</h3>}
      {!showBalance ? null : lots.length === 0 ? (
        <div className="admin-empty-state"><p>No prepaid balance.</p></div>
      ) : (
        <div className="admin-table-wrapper">
          <table className="admin-table dealer-table">
            <thead>
              <tr><th>Source</th><th className="num">Face value</th><th className="num">Remaining</th><th>Status</th><th>Expires</th><th aria-label="Actions" /></tr>
            </thead>
            <tbody>
              {lots.map((l) => (
                <tr key={l.id}>
                  <td>{humanize(l.source)}</td>
                  <td className="num">{aed(l.faceValueAed)}</td>
                  <td className="num">{aed(l.remainingAed)}</td>
                  <td><DealerStatusChip status={l.status} testId="lot-status" /></td>
                  <td>{formatDate(l.expiresAt)}</td>
                  <td className="dealer-actions">
                    {canRevokeAfterPaid && l.status === 'ACTIVE' && Number(l.remainingAed) > 0 && (
                      <button type="button" className="admin-btn admin-btn-danger admin-btn-sm" onClick={() => setRevoking(l)}>Revoke after paid</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h3 className="admin-card-title dealer-section-title">Refunds owed to the dealer</h3>
      {refunds.length === 0 ? (
        <div className="admin-empty-state"><p>No dealer refunds.</p></div>
      ) : (
        <div className="admin-table-wrapper">
          <table className="admin-table dealer-table">
            <thead>
              <tr><th>Credit note</th><th className="num">Revoked</th><th className="num">Owed</th><th>Status</th><th>Reason</th><th>Paid</th><th aria-label="Actions" /></tr>
            </thead>
            <tbody>
              {refunds.map((r) => (
                <tr key={r.id} data-testid={`refund-${r.id}`}>
                  <td>{r.creditNoteNumber ?? '—'}</td>
                  <td className="num">{aed(r.revokedAed)}</td>
                  <td className="num">{aed(r.owedAed)}</td>
                  <td><DealerStatusChip status={r.status} testId="refund-status" /></td>
                  <td className="dealer-cell-note">{r.reason ?? ''}</td>
                  <td className="dealer-cell-note">{r.status === 'PAID' ? `${r.bankReference ?? ''} · ${formatDate(r.valueDate)}` : ''}</td>
                  <td className="dealer-actions">
                    {r.status === 'DUE' && (
                      <button type="button" className="admin-btn admin-btn-primary admin-btn-sm" onClick={() => setPaying(r)}>Mark paid</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {revoking && (
        <RevokeAfterPaidDialog
          dealerId={dealerId}
          target={{ kind: 'lot', id: revoking.id, label: 'the dealer balance', remainingAed: revoking.remainingAed }}
          token={token}
          onClose={() => setRevoking(null)}
          onRevoked={(refund) => {
            setRevoking(null);
            notify(`Revoked ${aed(refund.revokedAed)}${refund.creditNoteNumber ? ` · credit note ${refund.creditNoteNumber}` : ''}`);
            load();
            onChanged?.();
          }}
          onFeatureOff={onFeatureOff}
        />
      )}
      {paying && (
        <RefundPaidDialog
          dealerId={dealerId}
          refund={paying}
          token={token}
          onClose={() => setPaying(null)}
          onDone={(message, type) => { setPaying(null); notify(message, type); load(); onChanged?.(); }}
        />
      )}
    </div>
  );
}

function RefundPaidDialog({ dealerId, refund, token, onClose, onDone }: {
  dealerId: string;
  refund: DealerRefund;
  token: string;
  onClose: () => void;
  onDone: (message: string, type?: 'success' | 'error') => void;
}) {
  const [bankReference, setBankReference] = useState('');
  const [valueDate, setValueDate] = useState(todayDubai());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (saving) return;
    const local: Record<string, string> = {};
    if (!bankReference.trim()) local.bankReference = 'Enter the bank reference';
    if (!/^\d{4}-\d{2}-\d{2}$/.test(valueDate)) local.valueDate = 'Pick the value date';
    setErrors(local);
    if (Object.keys(local).length) return;
    setSaving(true);
    setError(null);
    try {
      await dealersApi.markDealerRefundPaid(dealerId, refund.id, { bankReference: bankReference.trim(), valueDate }, token);
      onDone(`Refund of ${aed(refund.owedAed)} marked paid`);
    } catch (err) {
      if (dealerErrorCode(err) === 'ALREADY_PAID') { onDone(dealerErrorMessage(err), 'error'); return; }
      setError(dealerErrorMessage(err, 'Failed to mark the refund paid'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-modal-overlay">
      <div className="admin-modal admin-modal-sm" role="dialog" aria-modal="true" aria-label="Mark dealer refund paid">
        <div className="admin-modal-header"><h3 style={{ margin: 0 }}>Mark refund of {aed(refund.owedAed)} paid</h3></div>
        <form onSubmit={submit} noValidate>
          <div className="admin-modal-body">
            {error && <div className="admin-info-banner danger" role="alert">{error}</div>}
            <FormField id="refund-reference" label="Bank reference" error={errors.bankReference}>
              <input id="refund-reference" className="admin-form-input" maxLength={100} value={bankReference} disabled={saving}
                {...describedBy('refund-reference', errors.bankReference)} onChange={(e) => setBankReference(e.target.value)} />
            </FormField>
            <FormField id="refund-date" label="Value date" error={errors.valueDate}>
              <input id="refund-date" className="admin-form-input" type="date" value={valueDate} disabled={saving} style={{ maxWidth: '12rem' }}
                {...describedBy('refund-date', errors.valueDate)} onChange={(e) => setValueDate(e.target.value)} />
            </FormField>
          </div>
          <div className="admin-modal-footer">
            <button type="button" className="admin-btn admin-btn-outline" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="admin-btn admin-btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Mark paid'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
